/**
 * One matched asset (or near miss): total and part scores, kind and maturity, where it is available from, the deep
 * analysis (verdict, how it meets each need, gaps, risks) and the evidence it cites, each with its provenance in words.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { AdvisorMatch, MatchEvidence } from '@/api/types';
import { Badge } from '@/ui/Badge';
import { ScoreValue } from '@/ui/ScoreValue';

import { claimsLabel, verdictLabel } from './advisorUi';

function Part({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <span className="label">{label}</span>
      <div>{children}</div>
    </div>
  );
}

function List({ items }: { items: string[] }) {
  return <ul className="advisor-list">{items.map((item, i) => <li key={i}>{item}</li>)}</ul>;
}

function Evidence({ items }: { items: MatchEvidence[] }) {
  return (
    <ul className="advisor-evidence">
      {items.map((e, i) => (
        <li key={i}>
          <blockquote>{e.quote}</blockquote>
          <span className="advisor-evidence-source">
            {e.kind === 'metric' && e.provenance ? <Badge variant="provenance" value={e.provenance} /> : <Badge tone="neutral">Section</Badge>}
            <span className="hint">{e.label ?? (e.kind === 'metric' ? 'A metric no longer in the catalog' : 'A section no longer in the catalog')}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Scores({ match }: { match: AdvisorMatch }) {
  const { problem_fit, context_fit, evidence } = match.scores;
  return (
    <p className="hint advisor-scores">
      Problem fit <ScoreValue value={problem_fit} />, context fit <ScoreValue value={context_fit} />, evidence <ScoreValue value={evidence} />
    </p>
  );
}

function Deep({ match }: { match: AdvisorMatch }) {
  const { needs_met = [], gaps = [], risks = [], evidence = [] } = match;
  return (
    <div className="grid grid-2">
      {needs_met.length > 0 && (
        <Part label="How it meets the needs">
          <ul className="advisor-list">
            {needs_met.map((n, i) => <li key={i}><b>{n.need}</b>: {n.how}</li>)}
          </ul>
        </Part>
      )}
      <Part label="Gaps">{gaps.length ? <List items={gaps} /> : <span className="hint">None noted</span>}</Part>
      <Part label="Risks">{risks.length ? <List items={risks} /> : <span className="hint">None noted</span>}</Part>
      {evidence.length > 0 && <Part label="Evidence"><Evidence items={evidence} /></Part>}
    </div>
  );
}

function Head({ match }: { match: AdvisorMatch }) {
  const verdict = verdictLabel(match.verdict);
  return (
    <header className="advisor-match-head">
      <div className="advisor-match-total"><ScoreValue value={match.total} /></div>
      <div className="stack-sm">
        <h3 className="advisor-match-name">
          {match.archived ? match.name : <Link to={`/assets/${match.asset_id}`}>{match.name}</Link>}
        </h3>
        <div className="advisor-badges">
          <Badge variant="kind" value={match.kind} />
          <Badge variant="maturity" value={match.maturity} />
          {match.available_via && <Badge tone="info">Available via {match.available_via}</Badge>}
          {match.archived && <Badge tone="neutral">Archived</Badge>}
          {verdict && <Badge tone="neutral">{verdict}</Badge>}
        </div>
      </div>
    </header>
  );
}

export function MatchCard({ match, nearMiss = false }: { match: AdvisorMatch; nearMiss?: boolean }) {
  const claims = claimsLabel(match.measured_or_expected);
  return (
    <article className="card advisor-match" aria-label={match.name}>
      <div className="card-body stack-md">
        <Head match={match} />
        <Scores match={match} />
        <p>{match.reason}</p>
        {!nearMiss && (match.verdict ?? null) === null && (
          <p className="hint">The deep analysis did not run for this asset; the screening reason is all there is.</p>
        )}
        {!nearMiss && <Deep match={match} />}
        {claims && !nearMiss && <p className="hint">{claims}</p>}
      </div>
    </article>
  );
}
